import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { QueryObserverResult } from '@tanstack/query-core'
import { Observable } from 'rxjs'
import type { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'
import { injectMutation, injectQuery, injectQueryClient } from '../../query'
import { queryKeys } from '../../query-keys'
import { ToastService } from '../../toast.service'

export type MeetingEntityMinutesStatus = 'NOT_GENERATED' | 'ONGOING' | 'COMPLETED' | 'FAILED'

export type MeetingEntityInterval = {
  id: number
  /** The agenda item this is the entity's discussion of; null for the entity as a whole. */
  templateItemId: number | null
  startSeconds: number
  endSeconds: number
}

export type MeetingEntity = {
  id: number
  name: string
  orderIndex: number
  intervals: MeetingEntityInterval[]
  hasMinutes: boolean
  minutesStatus: MeetingEntityMinutesStatus
}

export type MeetingEntityTemplateItem = {
  templateItemId: number
  parentId: number | null
  title: string
  index: number
  isEntitySpecific: boolean
  applicableMeetingEntityIds: number[]
}

export type MeetingEntitiesOverview = {
  entities: MeetingEntity[]
  templateItems: MeetingEntityTemplateItem[]
  agendaEntityNamesToImport: string[]
  hasMeetingMinutes: boolean
}

export type MeetingEntityIntervalInput = {
  startSeconds: number
  endSeconds: number
}

const ONGOING_POLL_INTERVAL_MS = 5000

/** `hh:mm:ss`, `mm:ss` or plain seconds, as the meeting's timeline shows them; null for anything else. */
export function parseTimestamp(text: string): number | null {
  const parts = text.trim().split(':')
  if (parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) {
    return null
  }
  return parts.map(Number).reduce((total, part) => total * 60 + part, 0)
}

@Injectable({ providedIn: 'root' })
export class MeetingEntitiesService {
  private http = inject(HttpClient)
  private query = injectQuery()
  private mutation = injectMutation()
  private queryClient = injectQueryClient()
  private toastService = inject(ToastService)

  getOverview(meetingId: number): Observable<QueryObserverResult<MeetingEntitiesOverview>> {
    return this.query({
      queryKey: queryKeys.meetings.entities.byMeetingId(meetingId),
      queryFn: () => this.http.get<MeetingEntitiesOverview>(`/api/meetings/${meetingId}/entities`),
      refetchInterval: (query) => ((query.state.data?.entities ?? []).some((entity) => entity.minutesStatus === 'ONGOING') ? ONGOING_POLL_INTERVAL_MS : false),
    }).result$
  }

  getCitations(meetingId: number, meetingEntityId: number): Observable<QueryObserverResult<{ citations: CitationMinuteNodes | null }>> {
    return this.query({
      queryKey: queryKeys.meetings.entities.citations(meetingId, meetingEntityId),
      queryFn: () => this.http.get<{ citations: CitationMinuteNodes | null }>(`/api/meetings/${meetingId}/entities/${meetingEntityId}/citations`),
    }).result$
  }

  createEntityMutation() {
    return this.entityMutation(({ meetingId, name }: { meetingId: number; name: string }) =>
      this.http.post<{ id: number }>(`/api/meetings/${meetingId}/entities`, { name })
    )
  }

  importFromAgendaMutation() {
    return this.entityMutation(({ meetingId }: { meetingId: number }) =>
      this.http.post<{ createdCount: number }>(`/api/meetings/${meetingId}/entities/import-from-agenda`, {})
    )
  }

  renameEntityMutation() {
    return this.entityMutation(({ meetingId, meetingEntityId, name }: { meetingId: number; meetingEntityId: number; name: string }) =>
      this.http.patch<void>(`/api/meetings/${meetingId}/entities/${meetingEntityId}`, { name })
    )
  }

  reorderEntitiesMutation() {
    return this.entityMutation(({ meetingId, meetingEntityIds }: { meetingId: number; meetingEntityIds: number[] }) =>
      this.http.put<void>(`/api/meetings/${meetingId}/entities/order`, { meetingEntityIds })
    )
  }

  deleteEntityMutation() {
    return this.entityMutation(({ meetingId, meetingEntityId }: { meetingId: number; meetingEntityId: number }) =>
      this.http.delete<void>(`/api/meetings/${meetingId}/entities/${meetingEntityId}`)
    )
  }

  replaceIntervalsMutation() {
    return this.entityMutation(
      ({
        meetingId,
        meetingEntityId,
        templateItemId,
        intervals,
      }: {
        meetingId: number
        meetingEntityId: number
        templateItemId: number | null
        intervals: MeetingEntityIntervalInput[]
      }) => this.http.put<void>(`/api/meetings/${meetingId}/entities/${meetingEntityId}/intervals`, { templateItemId, intervals })
    )
  }

  updateTemplateItemScopeMutation() {
    return this.entityMutation(
      ({
        meetingId,
        templateItemId,
        isEntitySpecific,
        applicableMeetingEntityIds,
      }: {
        meetingId: number
        templateItemId: number
        isEntitySpecific: boolean
        applicableMeetingEntityIds: number[]
      }) => this.http.put<void>(`/api/meetings/${meetingId}/entities/template-items/${templateItemId}`, { isEntitySpecific, applicableMeetingEntityIds })
    )
  }

  generateEntityMinutesMutation() {
    return this.entityMutation(
      ({ meetingId, meetingEntityId }: { meetingId: number; meetingEntityId: number }) =>
        this.http.post<void>(`/api/meetings/${meetingId}/entities/${meetingEntityId}/generate-minutes`, {}),
      'MEETING_ENTITIES.GENERATION_STARTED'
    )
  }

  generateAllEntityMinutesMutation() {
    return this.entityMutation(
      ({ meetingId }: { meetingId: number }) => this.http.post<{ dispatchedCount: number }>(`/api/meetings/${meetingId}/entities/generate-minutes`, {}),
      'MEETING_ENTITIES.GENERATION_STARTED'
    )
  }

  private entityMutation<TVariables extends { meetingId: number }, TData>(
    mutationFn: (variables: TVariables) => Observable<TData>,
    successToastKey: string | null = null
  ) {
    return this.mutation({
      mutationFn,
      onSuccess: (_, { meetingId }) => {
        // The meeting's own key: its details list the entities too, and the entities' key is under it.
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        if (successToastKey !== null) {
          this.toastService.show(successToastKey)
        }
      },
      onError: () => {
        this.toastService.show('MEETING_ENTITIES.UPDATE_FAILED')
      },
    })
  }
}
